import { VARIABLE_IDS } from '@/components/admin/variables/contracts';
import { VariablePill } from '@/components/admin/variables/variable-pill';
import { cn } from '@/lib/utils';
import type { PaletteContent } from '@/lib/variable-palette';

/**
 * La palette `@` d'une expérience, et le bouton `{ }` qui l'ouvre aussi
 * (refonte modulaire §8.6, H61 ; grill Variables du 2026-09-16, D3 ; maquette
 * `variables.html`, planche 1 ; issue #88).
 *
 * §8.6 puts the same two gestures on every text zone - « insérées en tapant @
 * (liste filtrée) ou par le bouton {} » - so the LIST is one component and the
 * openings are the zones': a Texte block opens it from its WYSIWYG, a Checkbox
 * from its label, the Contexte and the overlay from their painted field, and
 * every one of them hands back the token to insert.
 *
 * What it offers is decided by `variablePalette`, pure and tested: the fields
 * and choices IN REACH where the zone stands, the objectives when the protocol
 * has matter there, the criteria always. Each line wears the pill it will
 * insert, so the ink is learnt before the choice. And the FOOT counts what is
 * not offered on purpose (H61): a variable declared after this page is not lost,
 * it is out of reach, and the menu says so rather than letting somebody search.
 *
 * An EMPTY menu says which gesture declares a variable rather than showing
 * nothing: « there are none yet » is only useful with what to do about it.
 *
 * A painted field that counts its text is bounded by it (#160), and a line
 * whose token would pass that bound where it lands is GREYED, never inserted
 * and cut (`fits`), under one sentence that says why (`full`) - the régime of
 * the directions' palette since #158.
 */

export interface VariableMenuProps {
  readonly content: PaletteContent;
  /**
   * The letters typed after the `@`, or null when the `{ }` button opened the
   * menu - which is what decides whether the query line is drawn.
   */
  readonly query: string | null;
  readonly onInsert: (token: string) => void;
  /** Whether a token still holds under the field's bound - absent, every line does. */
  readonly fits?: ((token: string) => boolean) | undefined;
  /** What the menu says above its lines when `fits` greys one of them. */
  readonly full?: string | undefined;
  readonly className?: string | undefined;
}

export function VariableMenu({
  content,
  query,
  onInsert,
  fits,
  full,
  className,
}: VariableMenuProps) {
  const refused = (token: string): boolean => fits !== undefined && !fits(token);
  const anyRefused = content.groups.some((group) =>
    group.entries.some((entry) => refused(entry.token)),
  );
  return (
    <div
      data-testid={VARIABLE_IDS.menu}
      className={cn(
        'max-h-72 space-y-2 overflow-y-auto rounded-md border-2 border-gray-200 bg-white p-2 shadow-lg',
        className,
      )}
    >
      {query === null ? null : (
        <p className="flex items-center gap-2 px-1 text-gray-500 text-caption">
          <span>
            @<span className="font-semibold text-gray-900">{query}</span>
          </span>
          <span className="ml-auto">Entrée insère · Échap ferme</span>
        </p>
      )}
      {anyRefused && full !== undefined ? (
        <p className="px-1 py-1 text-gray-700 text-caption leading-4">{full}</p>
      ) : null}
      {content.groups.length === 0 ? (
        <p className="px-1 py-1 text-gray-700 text-caption leading-4">
          Aucune variable à insérer ici : un input, une checkbox ou un bouton qui retient son choix
          sur une page précédente en pose une, et un protocole sur la page protocole apporte ses
          objectifs.
        </p>
      ) : (
        content.groups.map((group) => (
          <div key={group.title} className="space-y-1">
            <p className="px-1 font-semibold text-gray-500 text-caption uppercase tracking-wide">
              {group.title}
            </p>
            <ul>
              {group.entries.map((entry) => {
                const greyed = refused(entry.token);
                return (
                  <li key={entry.token}>
                    <button
                      type="button"
                      data-testid={VARIABLE_IDS.menuInsert}
                      // `aria-disabled` and not `disabled`: a disabled button
                      // swallows the mousedown below, and the field would lose
                      // its caret to a line that inserts nothing anyway.
                      aria-disabled={greyed ? true : undefined}
                      /*
                        `onMouseDown` and not `onClick`: a click steals the focus
                        from the surface first, and a `contentEditable` that loses
                        the focus loses its selection - so the token would land at
                        offset 0 instead of at the caret.
                      */
                      onMouseDown={(event) => {
                        event.preventDefault();
                        if (!greyed) onInsert(entry.token);
                      }}
                      className="flex w-full cursor-pointer items-baseline justify-between gap-3 rounded-sm px-1.5 py-1 text-left hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit aria-disabled:cursor-not-allowed aria-disabled:hover:bg-transparent"
                    >
                      <VariablePill
                        // A line that no longer fits wears the grey of a pill
                        // that resolves nothing: it inserts nothing either.
                        paint={{
                          label: `@${entry.label}`,
                          ink: greyed ? 'unknown' : entry.ink,
                          token: entry.token,
                        }}
                        interactive={false}
                        className="min-w-0 truncate text-caption leading-5"
                      />
                      {/* A homonym's meta is the one thing that tells two identical
                          pills apart, so it is inked rather than merely written. */}
                      <span
                        className={cn(
                          'shrink-0 text-caption',
                          entry.duplicate ? 'text-jaune-deep' : 'text-gray-600',
                        )}
                      >
                        {entry.meta}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}
      {content.footer.length === 0 ? null : (
        <div
          data-testid={VARIABLE_IDS.menuFooter}
          className="space-y-1 border-gray-200 border-t px-1 pt-2 text-gray-500 text-caption leading-4"
        >
          {content.footer.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}
